import { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Paper,
  Box,
  TextField,
  Stack,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { createPatient, getPatient, getPatientsFromVqura } from '../api/patientApi';
import { Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const Patient = () => {
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const [localRes, ordRes] = await Promise.all([
          getPatient(),
          getPatientsFromVqura(),
        ]);

        let localPatients = [];
        if (localRes.data.succeeded) {
          localPatients = localRes.data.data.map((p) => ({
            id: p.id,
            name: p.name,
            mobileNo: p.mobileNo,
            source: 'local',
          }));
        }

        let ordPatients = [];
        if (ordRes.data.items) {
          ordPatients = ordRes.data.items.map((p) => ({
            id: p.user_id,
            name: [p.first_name, p.last_name].filter(Boolean).join(' '),
            mobileNo: p.mobile_number,
            dateOfBirth: p.dob,
            email: p.email,
            gender: p.gender,
            address: `${p.street_address}, ${p.city}, ${p.state} - ${p.zip_code}`,
            source: 'vqura',
          }));
        }

        const merged = [...localPatients, ...ordPatients];

        setPatients(merged);
        setFilteredPatients(merged);
      } catch (error) {
        console.error('Error fetching patients:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPatients();
  }, []);


  useEffect(() => {
    const lowerSearch = search.toLowerCase();
    const filtered = patients.filter((patient) => {
      const name = patient.name?.toLowerCase() || '';
      const mobile = patient.mobileNo || '';

      return (
        name.includes(lowerSearch) ||
        mobile.includes(lowerSearch)
      );
    });
    setFilteredPatients(filtered);
  }, [search, patients]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      mobileNo: '',
      dateOfBirth: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      mobileNo: Yup.string()
        .matches(/^[0-9]{10}$/, 'Must be a 10-digit mobile number')
        .required('Mobile number is required'),
      dateOfBirth: Yup.date()
        .required('Date of birth is required')
        .max(new Date(), 'DOB must be in the past'),
    }),
    onSubmit: async (values, { resetForm }) => {
      const formattedValues = {
        ...values,
        dateOfBirth: dayjs(values.dateOfBirth).format('YYYY-MM-DD'),
      };
      console.log(formattedValues);
      const response = await createPatient(formattedValues);
      if (response.data.succeeded) {  
        const newPatient = {
          id: response.data.data,
          ...values,
        };
        setPatients((prev) => [...prev, newPatient]);
        setFilteredPatients((prev) => [...prev, newPatient]);
        resetForm();
        handleClose();
      }
      else {
        alert("There is a problem adding Patient, contact support team.");
      }
    },
  });

  return (
    <Container sx={{ mt: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h4" gutterBottom>
          Patient List
        </Typography>
        <Button
          variant="contained"
          onClick={handleOpen}
          sx={{
            backgroundColor: '#ea258b',
            '&:hover': { backgroundColor: '#d11e77' },
          }}
        >
          Add Patient
        </Button>
      </Stack>


      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          label="Search by Name or Mobile"
          variant="outlined"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={3}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>ID</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Mobile No</strong></TableCell>
                <TableCell><strong>Source</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPatients.length > 0 ? (
                filteredPatients.map((patient) => (
                  <TableRow key={patient.id}>
                    <TableCell>{patient.id}</TableCell>
                    <TableCell>{patient.name}</TableCell>
                    <TableCell>{patient.mobileNo}</TableCell>
                    <TableCell>{patient.source}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={2}>
                        <Link
                          component={RouterLink}
                          to="/domains"
                          state={{ patientId: patient.id, patientName: patient.name }}
                          underline="hover"
                          sx={{ color: '#ea258b', fontWeight: 500 }}
                        >
                          Assessment
                        </Link>
                        <Link
                          component={RouterLink}
                          to={`/patient/${patient.id}/history`}
                          underline="hover"
                          sx={{ color: '#ea258b', fontWeight: 500 }}
                        >
                          History
                        </Link>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No patients found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Add Patient Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Add Patient</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={formik.handleSubmit} noValidate>
            <TextField
              fullWidth
              label="Name"
              margin="normal"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
            <TextField
              fullWidth
              label="Mobile Number"
              margin="normal"
              name="mobileNo"
              value={formik.values.mobileNo}
              onChange={formik.handleChange}
              error={formik.touched.mobileNo && Boolean(formik.errors.mobileNo)}
              helperText={formik.touched.mobileNo && formik.errors.mobileNo}
            />
            <DatePicker
              label="Date of Birth"
              value={formik.values.dateOfBirth ? dayjs(formik.values.dateOfBirth) : null}
              onChange={(value) => formik.setFieldValue('dateOfBirth', value?.format('YYYY-MM-DD'))}
              maxDate={dayjs()} // disables future dates
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: 'normal',
                  error: formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth),
                  helperText: formik.touched.dateOfBirth && formik.errors.dateOfBirth,
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined" sx={{
              borderColor: '#ea258b',
              color:'#ea258b',
              '&:hover': { borderColor: '#d11e77' },
            }}>Cancel</Button>
          <Button onClick={formik.handleSubmit} variant="contained" sx={{
              backgroundColor: '#ea258b',
              '&:hover': { backgroundColor: '#d11e77' },
            }}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Patient;
